
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Company, KpiDefinition } from '../../types';
import { firestoreService } from '../../services/firestoreService';
import { defaultKpis } from '../../services/geminiService';
import Spinner from '../../components/common/Spinner';
import { AuthContext } from '../../contexts/AuthContext';

const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" /></svg>;
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>;
const SaveIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>;
const ResetIcon = () => <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>;

const ManageCompanyKpisPage: React.FC = () => {
    const { companyId } = useParams<{ companyId: string }>();
    const [company, setCompany] = useState<Company | null>(null);
    const [kpis, setKpis] = useState<KpiDefinition[]>([]);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const authContext = useContext(AuthContext);
    const tenantId = authContext?.user?.tenantId;

    const fetchData = useCallback(async () => {
        if (!companyId) return;
        setLoading(true);
        try {
            const fetchedCompany = await firestoreService.getCompany(companyId);
            const kpiProfile = await firestoreService.getCompanyKpiProfile(companyId);
            setCompany(fetchedCompany || null);
            setKpis(kpiProfile ? kpiProfile.kpis : defaultKpis);
        } catch (error) {
            console.error("Failed to fetch data", error);
        } finally {
            setLoading(false);
        }
    }, [companyId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleKpiChange = (index: number, field: keyof KpiDefinition, value: string) => {
        const newKpis = [...kpis];
        newKpis[index] = { ...newKpis[index], [field]: value };
        
        // Auto-generate ID from title if ID is empty or user is typing title
        if (field === 'title') {
             const generatedId = value.toLowerCase().replace(/[^a-z0-9]/g, '_');
             // Only update ID if it looks like it hasn't been manually heavily customized or if it's new
             newKpis[index].id = generatedId;
        }

        setKpis(newKpis);
        setHasChanges(true);
    };

    const handleAddKpi = () => {
        setKpis([...kpis, { id: '', title: '', description: '' }]);
        setHasChanges(true);
    };

    const handleRemoveKpi = (index: number) => {
        const newKpis = kpis.filter((_, i) => i !== index);
        setKpis(newKpis);
        setHasChanges(true);
    };

    const handleResetToDefault = () => {
        if (window.confirm("Are you sure you want to reset to the platform default KPIs? Current changes will be lost.")) {
            setKpis(defaultKpis);
            setHasChanges(true);
        }
    };

    const handleSave = async () => {
        if (!companyId || !tenantId) return;
        
        // Validation
        const emptyFields = kpis.some(k => !k.title || !k.description || !k.id);
        if (emptyFields) {
            alert("All fields (ID, Title, Prompt) are required for every KPI.");
            return;
        }
        
        // Duplicate ID check
        const ids = kpis.map(k => k.id);
        if (new Set(ids).size !== ids.length) {
            alert("KPI IDs must be unique.");
            return;
        }

        setIsSaving(true);
        try {
            // Fixed: Added tenantId to saveCompanyKpiProfile call
            await firestoreService.saveCompanyKpiProfile(tenantId, companyId, kpis);
            setHasChanges(false);
            alert("KPIs updated successfully! New videos will use this configuration.");
        } catch (error) {
            console.error("Error saving KPIs:", error);
            alert("Failed to save configuration.");
        } finally {
            setIsSaving(false);
        }
    };

    if (loading) return <div className="flex justify-center items-center h-full"><Spinner size="lg" /></div>;
    if (!company) return <div className="text-center text-red-400">Company not found.</div>;

    return (
        <div className="container mx-auto pb-20">
             <Link to="/kpis" className="text-blue-400 hover:text-blue-300 mb-6 inline-flex items-center gap-2">
                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
                 Back to Company List
            </Link>
            
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white">KPI Configuration: {company.name}</h1>
                    <p className="text-gray-400 mt-1">Define the criteria and instructions (prompts) the AI will use to evaluate videos for this company.</p>
                </div>
                <div className="flex gap-3">
                    <button 
                        onClick={handleResetToDefault} 
                        className="px-4 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-800 transition-colors flex items-center"
                    >
                        <ResetIcon /> Reset Defaults
                    </button>
                    <button 
                        onClick={handleSave} 
                        disabled={!hasChanges || isSaving}
                        className={`px-5 py-2 bg-green-600 text-white rounded-lg transition-colors font-semibold flex items-center ${(!hasChanges || isSaving) ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-700'}`}
                    >
                        {isSaving ? <Spinner size="sm" /> : <SaveIcon />}
                        Save Changes
                    </button>
                </div>
            </div>

            <div className="space-y-4">
                {kpis.map((kpi, index) => (
                    <div key={index} className="bg-gray-900 p-6 rounded-lg border border-gray-800 shadow-md transition-all hover:border-gray-700">
                        <div className="flex justify-between items-start gap-4">
                            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs uppercase text-gray-500 font-semibold mb-1">KPI Title (Display Name)</label>
                                    <input 
                                        type="text" 
                                        value={kpi.title} 
                                        onChange={(e) => handleKpiChange(index, 'title', e.target.value)}
                                        className="w-full bg-gray-800 text-white border border-gray-700 rounded p-2 focus:border-blue-500 focus:outline-none"
                                        placeholder="e.g. Customer Greeting"
                                    />
                                </div>
                                <div>
                                    <label className="block text-xs uppercase text-gray-500 font-semibold mb-1">Internal ID (Auto-generated)</label>
                                    <input 
                                        type="text" 
                                        value={kpi.id} 
                                        onChange={(e) => handleKpiChange(index, 'id', e.target.value)}
                                        className="w-full bg-gray-800 text-gray-400 border border-gray-700 rounded p-2 font-mono text-sm focus:border-blue-500 focus:outline-none"
                                        placeholder="customer_greeting"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-xs uppercase text-gray-500 font-semibold mb-1">AI Prompt Instruction</label>
                                    <textarea 
                                        value={kpi.description} 
                                        onChange={(e) => handleKpiChange(index, 'description', e.target.value)}
                                        className="w-full bg-gray-800 text-white border border-gray-700 rounded p-2 focus:border-blue-500 focus:outline-none h-24"
                                        placeholder="Describe specifically what the AI should look for and evaluate for this KPI..."
                                    />
                                    <p className="text-xs text-gray-500 mt-1">Be specific. Example: "Evaluate if the employee smiles and makes eye contact within the first 5 seconds."</p>
                                </div>
                            </div>
                            <button 
                                onClick={() => handleRemoveKpi(index)}
                                className="p-2 text-red-400 hover:bg-red-900/30 rounded-full transition-colors mt-6"
                                title="Remove KPI"
                            >
                                <TrashIcon />
                            </button>
                        </div>
                    </div>
                ))}

                <button 
                    onClick={handleAddKpi}
                    className="w-full py-4 border-2 border-dashed border-gray-700 rounded-lg text-gray-400 hover:text-white hover:border-gray-500 hover:bg-gray-900/50 transition-all flex justify-center items-center gap-2 font-medium"
                >
                    <PlusIcon /> Add New KPI
                </button>
            </div>
            
            <div className="mt-8 bg-blue-900/20 border border-blue-900/50 p-4 rounded-lg text-sm text-blue-200">
                <strong>Note:</strong> Changes made here will only apply to <em>new</em> video uploads and analyses. Existing video reports will retain the KPIs that were active at the time they were processed.
            </div>
        </div>
    );
};

export default ManageCompanyKpisPage;
